/**
 * ENG-6558 / ADR-0027: agent self-onboarding as a per-area state machine.
 *
 * A newly-provisioned agent onboards itself by walking a fixed sequence of
 * *areas of interest*, one at a time. Each area runs orient → ask → configure →
 * advance: the agent reads that area's current state, asks its manager the
 * focused question(s) for that area, performs the area's configure action, then
 * advances to the next applicable area. This module is the pure spine — the
 * step enum, the area order, and the reducer. It owns no I/O: the API seeds it
 * on first activation, persists the result to `agents.onboarding_state` (jsonb),
 * the agent advances it via MCP tools, and the manager nudges it per area.
 *
 * This supersedes ENG-6490's coarse `pending → interviewing → configuring →
 * ready` spine, which front-loaded the whole interview into one stage and
 * collapsed every configure concern into one opaque `configuring` blob (no
 * meaningful trail, nowhere for resume to land, nothing to drive one area at a
 * time). See docs/adr/0027-onboarding-per-area-state-machine.md.
 *
 * Linear walk, one per agent:
 *
 *   pending --START--> framing --ADVANCE--> tasks --ADVANCE--> integrations
 *           --ADVANCE--> reporting --ADVANCE--> ready
 *
 * ...with empty areas auto-skipped. There is deliberately NO `guardrails` area:
 * constraints and sensitive-area policy are inherited from org/team policy, not
 * gathered per-agent at onboarding time.
 *
 * Auto-skip: START and ADVANCE are parameterised by an `applicable` set the
 * caller (the API seam) computes live from agent data — `framing`/`reporting`
 * always apply; `tasks` applies iff the role has default tasks; `integrations`
 * applies iff there is a not-yet-connected recommendation. The reducer itself
 * stays ignorant of role/catalog data: it just walks AREA_ORDER and skips any
 * area not in `applicable`. When `applicable` is omitted it defaults to ALL
 * areas (no skipping) so the reducer is usable without the seam.
 *
 * Two operator re-entry events, surfaced as distinct slash commands (ENG-6511),
 * both gated like `/restart`:
 *
 *   - RESET is `/onboard-<code>`: a hard restart. From any area (or `ready`) it
 *     drops back to the first applicable area AND clears the `completed` trail,
 *     so onboarding runs again from the top. It never wipes the agent's config —
 *     the per-area orient + idempotent configure tools re-select interactively.
 *   - RESUME is `/resume-onboarding`: pick up where onboarding left off. It is
 *     idempotent — the current area and the trail are preserved (re-engage and
 *     re-orient the CURRENT area, never rewind). From `pending` it is illegal
 *     (nothing to resume); the host endpoint seeds the first area (START) there.
 *
 * The configure action of each area reconciles existing config rather than
 * duplicating it — that's a concern of the configure tools (orient-first +
 * idempotent writes keyed on `template_id` / `has_access`), not this machine,
 * which stays dumb about what each area actually does.
 */

/** The areas of interest onboarding walks, in order. */
export type OnboardingArea = 'framing' | 'tasks' | 'integrations' | 'reporting';

export type OnboardingStep = 'pending' | OnboardingArea | 'ready';

export type OnboardingEvent = 'START' | 'ADVANCE' | 'RESET' | 'RESUME';

/**
 * The fixed order onboarding walks its areas. Adding a future area is one entry
 * here plus its orient/question/configure wiring — no new events, no transition
 * surgery.
 */
export const AREA_ORDER: readonly OnboardingArea[] = [
  'framing',
  'tasks',
  'integrations',
  'reporting',
] as const;

const AREA_SET = new Set<string>(AREA_ORDER);

/** Type guard: is this step one of the walkable areas (not pending/ready)? */
export function isOnboardingArea(step: OnboardingStep): step is OnboardingArea {
  return AREA_SET.has(step);
}

/**
 * The channel onboarding was triggered from (ENG-6583). Onboarding is
 * manager-initiated (ENG-6578); the agent must hold the conversation HERE:
 * post each area's question to its manager in this channel and wait for the
 * reply before configuring/advancing, never self-answering. Anchored once when
 * onboarding starts/restarts and preserved across the walk so every area's
 * question lands in the same place the manager kicked it off.
 */
export interface OnboardingChannel {
  kind: 'slack' | 'telegram';
  /** Slack channel id / Telegram chat id (where to post the questions). */
  id: string;
  /** Optional thread anchor (e.g. Slack thread_ts) so replies stay threaded. */
  thread?: string;
}

/**
 * Human-readable posting target for the agent (ENG-6603). The directive and
 * onboarding_get name the channel KIND but must also hand the agent the concrete
 * id (and thread) so it posts to the exact channel onboarding was triggered from
 * instead of listing channels and guessing one. Slack -> "Slack channel `C…`
 * (thread `…`)"; Telegram -> "Telegram chat `…`".
 */
export function describeOnboardingChannel(channel: OnboardingChannel): string {
  if (channel.kind === 'slack') {
    return `Slack channel \`${channel.id}\`${channel.thread ? ` (thread \`${channel.thread}\`)` : ''}`;
  }
  return `Telegram chat \`${channel.id}\``;
}

/** Persisted shape of `agents.onboarding_state`. */
export interface OnboardingState {
  step: OnboardingStep;
  /** Areas left behind, in order — the per-area checklist of how we got here. */
  completed: OnboardingStep[];
  /**
   * The channel onboarding was triggered from (ENG-6583), set on START/RESET
   * from the initiator and preserved across ADVANCE/RESUME. Absent for legacy
   * rows and agent-session-driven onboarding with no channel context.
   */
  channel?: OnboardingChannel;
  /**
   * ISO time the agent ENTERED the current area (ENG-6602), stamped fresh on
   * every transition into a walkable area. The ask-and-wait gate compares it
   * against `agents.last_inbound_at`: ADVANCE is held until an inbound (the
   * manager's reply) arrives after this, so the agent can't self-advance through
   * areas in one turn. Absent on pending/ready and on pre-ENG-6602 rows (the
   * gate fails open when absent).
   */
  areaEnteredAt?: string;
  /**
   * Reset generation (ENG-6601). A monotonically increasing counter bumped on
   * every START and RESET and preserved across ADVANCE/RESUME. The host
   * onboarding-drive marker records the generation it injected for; the manager
   * re-injects when the generation changes EVEN ON THE SAME STEP, so `/onboard`
   * landing the agent back on the area it was already parked on (framing ->
   * framing) is no longer idempotency-swallowed. The naive "re-inject when
   * `completed == []`" shortcut can't do this: `completed` stays `[]` until the
   * agent advances past the first area, so it would re-inject every poll. Absent
   * on pre-ENG-6601 rows (treated as generation 0 by the host comparison).
   */
  generation?: number;
}

/** Options for {@link reduceOnboarding}. */
export interface ReduceOnboardingOptions {
  /**
   * The set of areas that currently have work to do, computed live by the
   * caller. START/ADVANCE skip any area not in this set; RESET lands on the
   * first applicable area. Omitted ⇒ every area is applicable (no skipping).
   */
  applicable?: Iterable<OnboardingArea>;
  /**
   * The channel onboarding was triggered from (ENG-6583). START/RESET anchor
   * the conversation here; ADVANCE/RESUME preserve whatever is already on the
   * state. Omitted on START/RESET ⇒ keep the existing channel (if any).
   */
  channel?: OnboardingChannel;
  /**
   * ISO timestamp to stamp as {@link OnboardingState.areaEnteredAt} when this
   * transition lands on a walkable area (ENG-6602): the moment the agent
   * enters/re-engages the area and starts waiting on its manager. Omitted ⇒ no
   * stamp (the wait gate then fails open for that state).
   */
  enteredAt?: string;
}

export const INITIAL_ONBOARDING_STATE: OnboardingState = { step: 'pending', completed: [] };

export class OnboardingTransitionError extends Error {
  constructor(
    public readonly step: OnboardingStep,
    public readonly event: OnboardingEvent,
  ) {
    super(`Invalid onboarding transition: ${event} from ${step}`);
    this.name = 'OnboardingTransitionError';
  }
}

function applicableSet(opts?: ReduceOnboardingOptions): Set<OnboardingArea> {
  return opts?.applicable ? new Set(opts.applicable) : new Set(AREA_ORDER);
}

/**
 * Spread the triggering channel onto a reduced state only when one is known,
 * keeping it OFF the persisted jsonb entirely (rather than `channel: undefined`)
 * for channel-less onboarding. `anchor` is the freshly-supplied channel
 * (START/RESET); `existing` is what was already on the state (preserved on
 * ADVANCE/RESUME, or kept on START/RESET when no new channel is supplied).
 */
function withChannel(
  base: OnboardingState,
  channel: OnboardingChannel | undefined,
): OnboardingState {
  return channel ? { ...base, channel } : base;
}

/**
 * Spread the reset generation onto a reduced state only when one is known
 * (ENG-6601), keeping it OFF the persisted jsonb for pre-ENG-6601 rows that
 * never carried one (rather than writing `generation: undefined`). START/RESET
 * pass a bumped number; ADVANCE/RESUME pass whatever the prior state had.
 */
function withGeneration(base: OnboardingState, generation: number | undefined): OnboardingState {
  return generation === undefined ? base : { ...base, generation };
}

/** Next reset generation: bump the prior one (absent ⇒ 0) by one (ENG-6601). */
function bumpGeneration(state: OnboardingState): number {
  return (state.generation ?? 0) + 1;
}

/**
 * Stamp {@link OnboardingState.areaEnteredAt} when the reduced state lands on a
 * walkable area (ENG-6602). Every transition into/onto an area resets the wait
 * clock, so a fresh `enteredAt` is applied; pending/ready carry no timestamp,
 * and an omitted `enteredAt` (legacy/test callers) leaves it unset.
 */
function stampEntered(base: OnboardingState, enteredAt: string | undefined): OnboardingState {
  return isOnboardingArea(base.step) && enteredAt ? { ...base, areaEnteredAt: enteredAt } : base;
}

/** First area in AREA_ORDER that is applicable, or `ready` if none are. */
function firstApplicableArea(applicable: Set<OnboardingArea>): OnboardingStep {
  return AREA_ORDER.find((a) => applicable.has(a)) ?? 'ready';
}

/** First applicable area strictly after `after`, or `ready` if none remain. */
function nextApplicableArea(after: OnboardingArea, applicable: Set<OnboardingArea>): OnboardingStep {
  const start = AREA_ORDER.indexOf(after) + 1;
  for (let i = start; i < AREA_ORDER.length; i++) {
    const area = AREA_ORDER[i]!;
    if (applicable.has(area)) return area;
  }
  return 'ready';
}

/**
 * Apply an event to the current state. Throws OnboardingTransitionError on an
 * illegal transition so callers never silently skip an area.
 *
 * - START   (only from `pending`): begin onboarding at the first applicable area.
 * - ADVANCE (only from an area): move to the next applicable area, else `ready`.
 *           The area being left behind is appended to `completed`.
 * - RESET   (from any area or `ready`): drop to the first applicable area and
 *           clear the trail. Illegal from `pending` (nothing to restart).
 * - RESUME  (from any area or `ready`): re-engage the current step idempotently —
 *           neither `step` nor the trail changes. Illegal from `pending`.
 */
export function reduceOnboarding(
  state: OnboardingState,
  event: OnboardingEvent,
  opts?: ReduceOnboardingOptions,
): OnboardingState {
  switch (event) {
    case 'START': {
      if (state.step !== 'pending') throw new OnboardingTransitionError(state.step, event);
      // Anchor to the triggering channel (ENG-6583); keep any existing one when
      // the caller supplies none. Stamp the area-entered time (ENG-6602). Bump
      // the reset generation so the host re-engages this fresh start (ENG-6601).
      return stampEntered(
        withGeneration(
          withChannel(
            {
              step: firstApplicableArea(applicableSet(opts)),
              completed: [...state.completed, 'pending'],
            },
            opts?.channel ?? state.channel,
          ),
          bumpGeneration(state),
        ),
        opts?.enteredAt,
      );
    }
    case 'ADVANCE': {
      if (!isOnboardingArea(state.step)) throw new OnboardingTransitionError(state.step, event);
      // Preserve the triggering channel across the walk so every area's
      // question lands in the same place onboarding was kicked off. The new area
      // gets a fresh area-entered time (ENG-6602): a new ask, a new wait. The
      // reset generation is preserved (ENG-6601): advancing is not a re-onboard.
      return stampEntered(
        withGeneration(
          withChannel(
            {
              step: nextApplicableArea(state.step, applicableSet(opts)),
              completed: [...state.completed, state.step],
            },
            state.channel,
          ),
          state.generation,
        ),
        opts?.enteredAt,
      );
    }
    case 'RESET': {
      // A hard restart from the top — clear the trail so `completed` stays
      // truthful. Illegal from `pending`: there is nothing to restart yet.
      // Re-anchor to the channel the restart came from (ENG-6583).
      if (state.step === 'pending') throw new OnboardingTransitionError(state.step, event);
      // Bump the reset generation so the host re-injects the directive even when
      // RESET lands back on the same area the agent was parked on (ENG-6601).
      return stampEntered(
        withGeneration(
          withChannel(
            { step: firstApplicableArea(applicableSet(opts)), completed: [] },
            opts?.channel ?? state.channel,
          ),
          bumpGeneration(state),
        ),
        opts?.enteredAt,
      );
    }
    case 'RESUME': {
      // Re-engage the current area without rewinding or losing progress —
      // idempotent. Return a fresh object so the reducer is uniformly
      // non-aliasing. Illegal from `pending`: nothing to resume. Keep the
      // existing channel, falling back to a freshly-supplied one if the state
      // never had one (legacy mid-walk rows).
      if (state.step === 'pending') throw new OnboardingTransitionError(state.step, event);
      // Re-engaging the area restarts the wait clock (ENG-6602): the agent
      // re-asks and waits afresh. The reset generation is preserved (ENG-6601):
      // RESUME is a continuation, not a re-onboard.
      return stampEntered(
        withGeneration(
          withChannel(
            { step: state.step, completed: [...state.completed] },
            state.channel ?? opts?.channel,
          ),
          state.generation,
        ),
        opts?.enteredAt,
      );
    }
    default:
      throw new OnboardingTransitionError(state.step, event as OnboardingEvent);
  }
}

export function isOnboardingComplete(state: OnboardingState): boolean {
  return state.step === 'ready';
}

const VALID_STEPS = new Set<string>(['pending', ...AREA_ORDER, 'ready']);

/**
 * Legacy ENG-6490 spine values, migrated on read by {@link coerceOnboardingState}:
 *  - `interviewing → framing` (the first area now leads the conversation);
 *  - `configuring → tasks` (the first of the configure areas).
 * Anything unrecognised resets to `pending` (re-onboard from the top).
 */
const LEGACY_STEP_REMAP: Record<string, OnboardingStep> = {
  interviewing: 'framing',
  configuring: 'tasks',
};

/** A valid current step is kept; a legacy step is remapped; else `null`. */
function remapStep(raw: string): OnboardingStep | null {
  if (VALID_STEPS.has(raw)) return raw as OnboardingStep;
  return LEGACY_STEP_REMAP[raw] ?? null;
}

/**
 * Coerce the persisted `agents.onboarding_state` jsonb into a valid state.
 * NULL (legacy / pre-seed), malformed, or partial blobs degrade to the initial
 * `pending` state rather than throwing — callers should never trust the column
 * shape blindly.
 *
 * ADR-0027 migration: legacy ENG-6490 spine values are remapped on read
 * (`interviewing → framing`, `configuring → tasks`); anything unrecognised in
 * the `step` resets to `pending` (re-onboard from the top). Trail entries are
 * remapped the same way, and any unrecognised trail entry is dropped — the fleet
 * is small and `/onboard` is re-runnable, so this needs no data migration.
 */
export function coerceOnboardingState(raw: unknown): OnboardingState {
  if (raw && typeof raw === 'object') {
    // The jsonb column can hold anything — type the fields as unknown rather
    // than trusting the declared OnboardingState shape.
    const s = raw as {
      step?: unknown;
      completed?: unknown;
      channel?: unknown;
      areaEnteredAt?: unknown;
      generation?: unknown;
    };
    if (typeof s.step === 'string' && Array.isArray(s.completed)) {
      const step = remapStep(s.step);
      // An unrecognised current step is genuinely ambiguous — re-onboard.
      if (step === null) return INITIAL_ONBOARDING_STATE;
      const completed = (s.completed as unknown[])
        .filter((c): c is string => typeof c === 'string')
        .map(remapStep)
        .filter((c): c is OnboardingStep => c !== null);
      // Preserve the reset generation (ENG-6601) when it's a valid non-negative
      // number; pre-ENG-6601 rows simply have none (omitted, treated as 0).
      const generation =
        typeof s.generation === 'number' && Number.isFinite(s.generation) && s.generation >= 0
          ? Math.floor(s.generation)
          : undefined;
      const out = withGeneration(
        withChannel({ step, completed }, coerceOnboardingChannel(s.channel)),
        generation,
      );
      // Preserve the area-entered timestamp (ENG-6602) only for a walkable area.
      const enteredAt =
        typeof s.areaEnteredAt === 'string' && s.areaEnteredAt ? s.areaEnteredAt : undefined;
      return enteredAt && isOnboardingArea(step) ? { ...out, areaEnteredAt: enteredAt } : out;
    }
  }
  return INITIAL_ONBOARDING_STATE;
}

/**
 * Validate the persisted `channel` blob (ENG-6583). A malformed or absent
 * channel degrades to `undefined` (channel-less onboarding) rather than
 * throwing (the column is jsonb and pre-ENG-6583 rows have no channel at all).
 */
function coerceOnboardingChannel(raw: unknown): OnboardingChannel | undefined {
  if (raw && typeof raw === 'object') {
    const ch = raw as { kind?: unknown; id?: unknown; thread?: unknown };
    if ((ch.kind === 'slack' || ch.kind === 'telegram') && typeof ch.id === 'string' && ch.id) {
      const channel: OnboardingChannel = { kind: ch.kind, id: ch.id };
      if (typeof ch.thread === 'string' && ch.thread) channel.thread = ch.thread;
      return channel;
    }
  }
  return undefined;
}

/**
 * Deadlock escape for the ask-and-wait gate (ENG-6602): if the manager never
 * replies, ADVANCE is allowed once the area has been outstanding this long, so a
 * ghosting manager can't wedge onboarding forever. Generous on purpose: the
 * normal unblock is the manager's reply, not this timeout.
 */
export const ONBOARDING_REPLY_TIMEOUT_MS = 24 * 60 * 60_000;

export interface OnboardingAdvanceGateInput {
  /** The area being advanced FROM (the current step). */
  step: OnboardingStep;
  /** When the agent entered/asked the current area ({@link OnboardingState.areaEnteredAt}). */
  areaEnteredAt?: string;
  /** The onboarding channel, used to require the reply arrived on the same channel kind. */
  channel?: OnboardingChannel;
  /** `agents.last_inbound_at`: when any inbound last reached the agent. */
  lastInboundAt?: string | null;
  /** `agents.last_inbound_source_integration`: which channel that inbound came on. */
  lastInboundSource?: string | null;
  /** Current time in ms (injected for testability). */
  nowMs: number;
  /** Override the deadlock-escape window (default {@link ONBOARDING_REPLY_TIMEOUT_MS}). */
  replyTimeoutMs?: number;
}

export interface OnboardingAdvanceGateResult {
  /** Whether ADVANCE is permitted. */
  allowed: boolean;
  /** Agent-facing explanation when blocked. */
  reason?: string;
  /** Allowed only because the wait timed out (the manager never replied). */
  viaTimeout?: boolean;
}

/**
 * The ENG-6602 ask-and-wait gate. Onboarding is a conversation: the agent asks
 * its manager the current area's question and must WAIT for a reply before
 * advancing. There is no server-side "manager replied" signal, so we use the
 * best available proxy: an inbound message arriving on the onboarding channel
 * AFTER the agent entered the area. A self-driving agent does
 * `onboarding_get → onboarding_advance` in a single turn with no intervening
 * inbound (blocked); a waiting agent advances in a later turn triggered by the
 * manager's reply (allowed). Fails OPEN when the state predates the gate
 * (`areaEnteredAt` absent) and escapes after {@link ONBOARDING_REPLY_TIMEOUT_MS}
 * so a non-responsive manager never deadlocks onboarding.
 */
export function onboardingAdvanceGate(input: OnboardingAdvanceGateInput): OnboardingAdvanceGateResult {
  const { step, areaEnteredAt, channel, lastInboundAt, lastInboundSource, nowMs } = input;
  const replyTimeoutMs = input.replyTimeoutMs ?? ONBOARDING_REPLY_TIMEOUT_MS;

  // Only walkable areas are gated; pending/ready aren't ADVANCE-from-area cases.
  if (!isOnboardingArea(step)) return { allowed: true };
  // No entry stamp ⇒ state predates ENG-6602 (or a caller didn't stamp). Fail
  // open rather than wedge onboarding that started before the gate existed.
  if (!areaEnteredAt) return { allowed: true };
  const enteredMs = Date.parse(areaEnteredAt);
  if (!Number.isFinite(enteredMs)) return { allowed: true };

  // Reply observed: an inbound arrived after we entered the area. When the
  // onboarding channel is known, require the inbound source to MATCH it: a
  // source-less or different-channel inbound (e.g. a direct-chat ping) must not
  // unblock a Slack/Telegram ask. A genuinely source-less inbound on a
  // channel-bound wait falls through to the timeout escape, never deadlocks.
  if (lastInboundAt) {
    const inboundMs = Date.parse(lastInboundAt);
    const channelMatches = !channel || lastInboundSource === channel.kind;
    if (Number.isFinite(inboundMs) && inboundMs > enteredMs && channelMatches) {
      return { allowed: true };
    }
  }

  // Deadlock escape: never wedge forever if the manager never replies.
  if (nowMs - enteredMs >= replyTimeoutMs) return { allowed: true, viaTimeout: true };

  return {
    allowed: false,
    reason:
      "I've asked my manager this area's question and I'm waiting for their reply " +
      'before moving on. I will advance automatically once they respond; I should ' +
      'not self-answer or skip ahead.',
  };
}
